import { enableProdMode, ErrorHandler, APP_INITIALIZER, importProvidersFrom } from '@angular/core';

import { environment } from './environments/environment';
import * as Sentry from '@sentry/angular';
import { browserTracingIntegration } from '@sentry/angular';
import { FileUploadService } from './app/core/services/file-upload.service';
import { UsersDataProvider } from './app/core/services/data-providers/users.data-provider';
import { DeliveryRatesService } from './app/core/services/delivery-rates.service';
import { ModalService } from './app/core/services/modal.service';
import { LoginService } from './app/core/services/login.service';
import { RecoveryService } from './app/core/services/recovery.service';
import { LoaderService } from './app/core/services/loader.service';
import { CompanyService } from './app/core/services/company.service';
import { UserAgreementDataProvider } from './app/core/services/data-providers/user-agreement.data-provider';
import { OrdersDataProvider } from './app/core/services/data-providers/orders.data-provider';
import { MenuAndMealsDataProvider } from './app/core/services/data-providers/menu-and-meals.data-provider';
import { CompaniesDataProvider } from './app/core/services/data-providers/companies.data-provider';
import { AppVersionDataProvider } from './app/core/services/data-providers/app-version.data-provider';
import { DI_COMPANIES_PROVIDER } from './app/core/services/data-providers/interfaces/companies-provider.interface';
import {
  CompaniesRefreshCurrentCompanyDataProvider,
  CompanyOptionsRefreshCurrentCompanyDataProvider
} from './app/core/services/extended-data-providers/refresh-current-company.providers';
import { MerchantsDataProvider } from './app/core/services/data-providers/merchants.data-provider';
import { DeliveryDaysDataProvider } from './app/core/services/data-providers/delivery-days.data-provider';
import { PicturesDataProvider } from './app/core/services/data-providers/pictures.data-provider';
import { CategoriesDataProvider } from './app/core/services/data-providers/categories.data-provider';
import { AddonsDataProvider } from './app/core/services/data-providers/addons.data-provider';
import { VariationsDataProvider } from './app/core/services/data-providers/variations.data-provider';
import { ReportsService } from './app/core/services/reports.service';
import { CompanyOptionsDataProvider } from './app/core/services/data-providers/company-options.data-provider';
import { DI_COMPANY_OPTIONS_PROVIDER } from './app/core/services/data-providers/interfaces/company-options-provider.interface';
import { CompanyOptionNamesDataProvider } from './app/core/services/data-providers/company-option-names.data-provider';
import { withInterceptors, provideHttpClient, HttpClient } from '@angular/common/http';
import { authInterceptor } from './app/core/interceptors/auth.interceptor';
import { RxStompService } from './app/shared/helpers/stomp-ws/rx-stomp.service';
import { LanguageService } from './app/shared/services/language.service';
import { MatildaTranslateLoader } from './app/shared/services/matilda-translate-loader.service';
import { Router, provideRouter } from '@angular/router';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { appRoutes } from './app/app.routing.module';
import { QRCodeModule } from 'angularx-qrcode';
import {
  TranslateModule,
  TranslateLoader,
  MissingTranslationHandler,
  MissingTranslationHandlerParams
} from '@ngx-translate/core';
import { AppComponent } from './app/app.component';

class KeyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    return params.key;
  }
}

// Turn off for ng serve
if (environment?.sentryDsn && environment.production) {
  Sentry.init({
    dsn: environment.sentryDsn,
    integrations: [browserTracingIntegration()],
    tracePropagationTargets: [/.*/],
    tracesSampleRate: 0.5
  });
}
if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      MatSnackBarModule,
      QRCodeModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useExisting: MatildaTranslateLoader,
          deps: [HttpClient]
        },
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: KeyMissingTranslationHandler
        }
      })
    ),
    FileUploadService,
    UsersDataProvider,
    DeliveryRatesService,
    ModalService,
    LoginService,
    RecoveryService,
    LoaderService,
    CompanyService,
    UserAgreementDataProvider,
    OrdersDataProvider,
    MenuAndMealsDataProvider,
    CompaniesDataProvider,
    AppVersionDataProvider,
    { provide: DI_COMPANIES_PROVIDER, useClass: CompaniesRefreshCurrentCompanyDataProvider },
    MerchantsDataProvider,
    DeliveryDaysDataProvider,
    PicturesDataProvider,
    CategoriesDataProvider,
    AddonsDataProvider,
    VariationsDataProvider,
    ReportsService,
    CompanyOptionsDataProvider,
    {
      provide: DI_COMPANY_OPTIONS_PROVIDER,
      useClass: CompanyOptionsRefreshCurrentCompanyDataProvider
    },
    CompanyOptionNamesDataProvider,
    RxStompService,
    LanguageService,
    MatildaTranslateLoader,
    // Sentry
    ...(environment.production
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
              showDialog: false
            })
          },
          {
            provide: Sentry.TraceService,
            deps: [Router]
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true
          }
        ]
      : []),
    provideRouter(appRoutes),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor]))
  ]
}).catch(err => console.error(err));
